import { parseModuleActivityModuleId } from '@modules/_shared/components/feed-v2/utils/moduleActivityParser';
import { ModuleId } from '@shared/constants/module';

type ModuleActivity = {
  id: string;
  activity_type: string;
  created_by?: string | null;
  done_at?: string | null;
  extra_data?: unknown;
};

type ModuleGrant = {
  module_id: string;
  granted_at?: string | null;
};

export type ModuleHistoryEvent = {
  id: string;
  module_id: string;
  type: 'grant' | 'revoke';
  created_by: string | null;
  created_at: string | null;
  before_tracking: boolean;
};

// Includes the catalog's financing module, which is not manually assignable in CRM.
export const MODULE_HISTORY_CATALOG = [
  ModuleId.Visibility,
  ModuleId.Leads,
  ModuleId.ProspectionMap,
  ModuleId.ProspectionMapV2,
  ModuleId.Brokerage,
  ModuleId.CmaReports,
  ModuleId.ValuationLink,
  ModuleId.AiChat,
  ModuleId.Financing,
] as const;

const MODULE_ACTIVITY_EVENT_TYPES: Record<string, ModuleHistoryEvent['type']> =
  {
    module_granted: 'grant',
    module_revoked: 'revoke',
  };

export const buildModuleHistory = (
  activities: readonly ModuleActivity[],
  moduleGrants: readonly ModuleGrant[],
): ModuleHistoryEvent[] => [
  ...activities.flatMap(activity => {
    const type = MODULE_ACTIVITY_EVENT_TYPES[activity.activity_type];
    const moduleId = parseModuleActivityModuleId(activity.extra_data);
    return type != null && moduleId != null
      ? [
          {
            id: activity.id,
            module_id: moduleId,
            type,
            created_by: activity.created_by ?? null,
            created_at: activity.done_at ?? null,
            before_tracking: false,
          },
        ]
      : [];
  }),
  ...moduleGrants.flatMap(grant =>
    grant.granted_at == null
      ? [
          {
            id: `before-tracking:${grant.module_id}`,
            module_id: grant.module_id,
            type: 'grant' as const,
            created_by: null,
            created_at: null,
            before_tracking: true,
          },
        ]
      : [],
  ),
];

export const sortModuleHistory = <T extends ModuleHistoryEvent>(
  events: readonly T[],
) =>
  [...events].sort((a, b) => {
    if (a.before_tracking !== b.before_tracking) {
      return a.before_tracking ? 1 : -1;
    }
    if (a.created_at && b.created_at) {
      return (
        Date.parse(b.created_at) - Date.parse(a.created_at) ||
        b.id.localeCompare(a.id)
      );
    }
    if (a.created_at) {
      return -1;
    }
    if (b.created_at) {
      return 1;
    }
    return b.id.localeCompare(a.id);
  });

export const groupModuleHistory = <T extends ModuleHistoryEvent>(
  events: readonly T[],
  activeModuleIds: readonly string[],
  getModuleName: (moduleId: string) => string,
  locale?: string,
) => {
  const groups = new Map<string, T[]>(
    MODULE_HISTORY_CATALOG.map(moduleId => [moduleId, []]),
  );
  const catalogIds = new Set<string>(MODULE_HISTORY_CATALOG);
  const active = new Set(activeModuleIds);
  const moduleNameCollator = new Intl.Collator(locale);
  const historicalModuleIds = [
    ...new Set([...events.map(event => event.module_id), ...active]),
  ]
    .filter(moduleId => !catalogIds.has(moduleId))
    .sort(
      (a, b) =>
        moduleNameCollator.compare(getModuleName(a), getModuleName(b)) ||
        a.localeCompare(b),
    );
  for (const moduleId of historicalModuleIds) {
    groups.set(moduleId, []);
  }
  for (const event of sortModuleHistory(events)) {
    groups.get(event.module_id)?.push(event);
  }
  return [...groups].map(([moduleId, moduleEvents]) => ({
    moduleId,
    events: moduleEvents,
    active: active.has(moduleId),
  }));
};
